import { faSync, faTimes } from "@fortawesome/free-solid-svg-icons";
import React, { useContext } from "react";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import Button from "../components/Button";
import CenterContainer from "../components/CenterContainer";
import { FormLabel, FormRow, FormText } from "../components/FormElements";
import Title from "../components/Title";
import {
    UserPasswordResetRequest,
    UserPasswordResetFinishRequest,
    UserPasswordResetFinishRequestSchema,
    UserPasswordResetRequestSchema,
} from "consumerhouse-shared";
import { Translation, useLanguage } from "../useLanguage";
import { AnyListener, useForm } from "typed-react-form";
import AuthContext from "../AuthContext";
import { ButtonContainer } from "../components/ButtonContainer";
import { RateLimitError } from "../api";

export function ResetPassword() {
    const history = useHistory();
    const language = useLanguage();
    const { client } = useContext(AuthContext);
    const form = useForm<UserPasswordResetRequest>({ email: "" }, (data) => UserPasswordResetRequestSchema.validate(data));

    async function reset() {
        await form.validate();
        if (form.error) return;

        form.setState({ isSubmitting: true });
        try {
            let res = await client.fetch("/user/passwordreset", form.values);
            if (res.status === "error") {
                form.setErrors({
                    email: res.email ? language(res.email as Translation) : undefined,
                });
            } else {
                alert(language("reset-password-pending").replace("{email}", form.values.email));
                history.push("/login");
            }
        } catch (ex) {
            if (ex instanceof RateLimitError) {
                form.setError("email", language("reset-password-limit"));
            } else {
                console.error("Could not reset password", ex);
            }
        }
        form.setState({ isSubmitting: false });
    }

    return (
        <CenterContainer width="500px" style={{ margin: "2em 0" }}>
            <Title>{language("reset-password")}</Title>
            <form
                onSubmit={(ev) => {
                    ev.preventDefault();
                    reset();
                }}>
                <FormRow>
                    <FormLabel>Email</FormLabel>
                    <FormText
                        style={{
                            flexGrow: 1,
                        }}
                        form={form}
                        name="email"
                        type="email"
                    />
                </FormRow>
                <hr />
                <ButtonContainer>
                    <Button onClick={() => history.push("/")} style={{ margin: "0.2em" }} type="button" color="#666" icon={faTimes}>
                        {language("reset-password-cancel")}
                    </Button>
                    <AnyListener
                        form={form}
                        render={({ error, state }) => (
                            <Button style={{ margin: "0.2em" }} disabled={state.isSubmitting} loading={state.isSubmitting} icon={faSync}>
                                {language("reset-password-reset")}
                            </Button>
                        )}
                    />
                </ButtonContainer>
            </form>
        </CenterContainer>
    );
}

export function FinishResetPassword(props: RouteComponentProps<{ s: string }>) {
    const form = useForm({ newPassword: "", token: decodeURIComponent(props.match.params.s) } as UserPasswordResetFinishRequest, (data) =>
        UserPasswordResetFinishRequestSchema.validate(data)
    );
    const language = useLanguage();
    const { client, setAuth } = useContext(AuthContext);
    const history = useHistory();

    async function submit() {
        await form.validate();
        if (form.error) return;

        form.setState({ isSubmitting: true });

        try {
            let res = await client.fetch("/user/passwordreset/finish", form.values);
            if (res.status === "ok") {
                setAuth(res);
                history.push("/account");
            } else {
                form.setError("newPassword", language(res.token as Translation));
            }
        } catch (ex) {
            if (ex instanceof RateLimitError) {
                form.setError("newPassword", language("reset-password-limit"));
            } else {
                console.error("Could not reset password", ex);
            }
        }

        form.setState({ isSubmitting: false });
    }

    return (
        <CenterContainer width="500px" style={{ margin: "2em 0" }}>
            <Title>{language("reset-password")}</Title>
            <form
                onSubmit={(ev) => {
                    ev.preventDefault();
                    submit();
                }}>
                <FormRow>
                    <FormLabel>{language("reset-password-newpassword")}</FormLabel>
                    <FormText form={form} name="newPassword" type="password" />
                </FormRow>
                <hr />
                <ButtonContainer>
                    <Button type="button" color="#666" icon={faTimes} onClick={() => history.push("/")}>
                        {language("reset-password-cancel")}
                    </Button>
                    <AnyListener
                        form={form}
                        render={({ error, state }) => (
                            <Button disabled={state.isSubmitting} loading={state.isSubmitting} icon={faSync}>
                                {language("reset-password-reset")}
                            </Button>
                        )}
                    />
                </ButtonContainer>
            </form>
        </CenterContainer>
    );
}
